import { useMemo } from 'react';
import {
  getNextPageParam,
  getWorksFromData,
  IFlow,
  IWork,
  makeDynamicWorkboardPageStore,
  PageViewModeEnum,
  performanceWorkboardCountQuery,
  performanceWorkQuery,
  SortModel,
  useAbortRequestWhenFiltersValuesChanged,
  usePerformanceGraphQLParameters,
  useOfflineCreatedWorks,
} from '@diana/core';
import { getWorkboardGraphQLWorksCountQueryKey, getWorkboardGraphQLWorksQueryKey } from './Support';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

export function useWorkboardQuery(params: IUseWorkboardQueryParams) {
  const { flow, activeStepIds, viewMode, sortModel, enabled = true } = params;
  const isTableExpandedViewMode = viewMode === PageViewModeEnum.TableExpanded;

  const {
    queryKeys,
    queryParameters: { sortField, sortDirection, ...queryParameters },
  } = useWorkboardQueryParams({ flow, activeStepIds, viewMode, sortModel });

  const { data, ...rest } = useInfiniteQuery<IWork[]>({
    queryKey: queryKeys,
    queryFn: async ({ pageParam }) => {
      return await performanceWorkQuery
        .get({
          ...queryParameters,
          isExpandedFeatureEnabled: isTableExpandedViewMode,
          removeDeletedItems: true,
        })
        .fn({ skip: pageParam as number, sortField, sortDirection });
    },
    enabled: enabled && Boolean(flow),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: getNextPageParam,
  });

  const offlineCreatedWorks = useOfflineCreatedWorks({ flowId: flow.id, activeStepIds });

  const works = useMemo(() => {
    const normalWorks = getWorksFromData({ data, flow });
    return offlineCreatedWorks.length > 0 ? [...offlineCreatedWorks, ...normalWorks] : normalWorks;
  }, [data, flow, offlineCreatedWorks]);

  return {
    ...rest,
    queryKeys,
    data: works,
  };
}

export function useWorkboardColumnCountQuery(params: Omit<IUseWorkboardQueryParams, 'viewMode'>) {
  const { flow, activeStepIds, sortModel, enabled = true } = params;

  const {
    queryParameters: { sortField, sortDirection, ...queryParameters },
  } = useWorkboardQueryParams({ flow, activeStepIds, viewMode: PageViewModeEnum.Board, sortModel });

  return useQuery({
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    queryKey: getWorkboardGraphQLWorksCountQueryKey({
      flowId: flow.id,
      activeStepIds,
      graphQLFilters: queryParameters.filterProperties,
    }),
    queryFn: async () => {
      return await performanceWorkboardCountQuery
        .get({
          ...queryParameters,
          isExpandedFeatureEnabled: false,
          removeDeletedItems: true,
        })
        .fn();
    },
  });
}

export function useWorkboardQueryParams(params: IUseWorkboardQueryParams) {
  const { flow, activeStepIds, viewMode, sortModel } = params;

  const useWorkboardPageStore = makeDynamicWorkboardPageStore(flow.flowOriginId);
  const availableFilters = useWorkboardPageStore((store) => store.availableFilters);
  const filtersValues = useWorkboardPageStore((store) => store.filtersValues);

  const { abort } = useAbortRequestWhenFiltersValuesChanged({
    filtersValues,
    sortModel,
    abortOnUnmount: false,
  });

  const queryParameters = usePerformanceGraphQLParameters({
    flows: [flow],
    filters: availableFilters,
    filtersValues: filtersValues,
    activeStepIds,
    abort,
    sortModel,
    showCompleted: true,
    showDeleted: false,
    showInProgress: true,
  });

  const queryKeys = getWorkboardGraphQLWorksQueryKey(
    flow.id,
    queryParameters.filterProperties,
    viewMode,
    activeStepIds,
    sortModel,
  );

  return {
    queryKeys,
    queryParameters,
  };
}

export interface IUseWorkboardQueryParams {
  flow: IFlow;
  activeStepIds?: string[];
  viewMode: PageViewModeEnum;
  sortModel?: SortModel;
  enabled?: boolean;
}
